import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { LuSparkles, LuSlidersHorizontal } from 'react-icons/lu';
import { MdLinkOff } from 'react-icons/md';
import { Button } from '@/components/Button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type SharedAiSourceSelection from '@/types/serializers/SharedAiSourceSelection';
import useAiJobSearchFlow from '../../ai_job_search/hooks/useAiJobSearchFlow';
import { DEFAULT_SELECTED_SOURCES } from '../../ai_job_search/constants';
import type { ReferenceSelection } from '../types/referenceSelection';
import RunStatusPanel from './RunStatusPanel';
import RecommendationList from './RecommendationList';
import AICustomizeDialog from './AICustomizeDialog';
import SuggestionConfirmDialog from './SuggestionConfirmDialog';
import useFetchAiRecommendationReferenceItems from '../api/useFetchAiRecommendationReferenceItems';
import useSuggestAiRecommendations from '../api/useSuggestAiRecommendations';
import { showErrorToast, showSuccessToast } from '../../../job_change_processes/lib/toast';

type Props = {
  candidateProfileId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuggested?: () => void;
};

/**
 * AIでおすすめ求人を探すダイアログ（UIは旧 Qdrant 推薦のものを踏襲、中身は新しい適応型検索）
 *   - 初回: idle / ローディング / エラー → コンパクト表示
 *   - 結果表示: 広レイアウトで結果リスト
 *   - 再生成中（前回結果あり）: 広レイアウトのままローディング
 *
 * バックエンドは useAiJobSearchFlow（ペルソナ構築 → 求人検索）。スコア・推薦理由・
 * 検索クエリ確認は表示しない（関連度順のみ）。
 */
export default function AIRecommendationsPanel({
  candidateProfileId,
  open,
  onOpenChange,
  onSuggested,
}: Props) {
  const {
    stage,
    personaData,
    resultsData,
    isProcessing,
    isRestoring,
    isResultsLoading,
    personaProgressMessage,
    searchProgressMessage,
    personaErrorMessage,
    searchErrorMessage,
    startPersonaBuild,
    rebuildCurrentPersona,
    retryPersonaBuild,
  } = useAiJobSearchFlow(candidateProfileId, open);

  // 選択中の result.id Set
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // 直近に入力したプロンプト（カスタマイズダイアログの初期値に利用）
  const [lastPrompt, setLastPrompt] = useState('');

  // 求人提案の最終確認モーダルの表示状態
  const [isConfirmingSubmission, setIsConfirmingSubmission] = useState(false);

  // 「AIをカスタマイズして再検索」モーダルの表示状態
  const [isCustomizing, setIsCustomizing] = useState(false);

  // このダイアログを開いている間に一度でも結果が出たかを追跡（再生成中レイアウト制御）
  const hasEverHadResultsRef = useRef(false);

  const suggestMutation = useSuggestAiRecommendations(candidateProfileId);

  const hasResults = Boolean(stage === 'search_completed' && !isResultsLoading && resultsData);
  const isFailed = stage === 'persona_failed' || stage === 'search_failed';
  const progressMessage = searchProgressMessage ?? personaProgressMessage;
  const errorMessage = personaErrorMessage ?? searchErrorMessage;

  if (hasResults) {
    hasEverHadResultsRef.current = true;
  }

  // ダイアログを閉じたタイミングでローカル状態を初期化（次回オープン時に持ち越さない）
  useEffect(() => {
    if (!open) {
      hasEverHadResultsRef.current = false;
      setLastPrompt('');
      setSelectedIds(new Set());
      setIsConfirmingSubmission(false);
      setIsCustomizing(false);
    }
  }, [open]);

  // 再生成中かどうか: 処理中 + これまでに一度でも結果が出ている
  const isRerunning = isProcessing && hasEverHadResultsRef.current;
  // 広いレイアウトを使うか: 結果表示中 or 再生成中
  const useWideLayout = hasResults || isRerunning;

  // 結果から選択中のものを取り出す
  const selectedResults = useMemo(() => {
    if (!resultsData) return [];
    return resultsData.results.filter((r) => selectedIds.has(r.id));
  }, [resultsData, selectedIds]);

  const selectedCount = selectedIds.size;
  const canSubmit = hasResults && selectedCount > 0 && !suggestMutation.isPending;

  // カスタマイズダイアログのプリセット用に参照情報の候補一覧を取得する
  // （ダイアログ側と同一 queryKey のため React Query のキャッシュを共有する）。
  const { data: referenceItems } = useFetchAiRecommendationReferenceItems(
    open ? candidateProfileId : null
  );

  // カスタマイズダイアログに渡す初期選択。直近の検索（現在のペルソナ）で実際に参照した
  // 項目をプリセットする。source_selection が個別指定ありならそのIDを、未指定でも
  // カテゴリ自体が selected_sources に含まれていれば「全項目」を選択済みにする
  // （バックエンドは未指定カテゴリを全件参照するため、それを UI に反映する）。
  const defaultSelection: ReferenceSelection = useMemo(() => {
    const sourceSelection = personaData?.source_selection;
    const selectedSources = personaData?.selected_sources ?? [];

    const allCustomFieldIds =
      referenceItems?.candidate_info_categories.flatMap((category) =>
        category.custom_fields.map((field) => field.id)
      ) ?? [];
    const allInterviewLogIds = referenceItems?.interview_logs.map((log) => log.id) ?? [];

    const customFieldIds =
      sourceSelection?.custom_data && sourceSelection.custom_data.length > 0
        ? sourceSelection.custom_data
        : selectedSources.includes('custom_data')
          ? allCustomFieldIds
          : [];
    const interviewLogIds =
      sourceSelection?.candidate_interview_log && sourceSelection.candidate_interview_log.length > 0
        ? sourceSelection.candidate_interview_log
        : selectedSources.includes('candidate_interview_log')
          ? allInterviewLogIds
          : [];

    return { customFieldIds, interviewLogIds };
  }, [personaData, referenceItems]);

  const handleConfirmSuggest = useCallback(() => {
    if (selectedResults.length === 0) return;

    suggestMutation.mutate(selectedResults, {
      onSuccess: () => {
        showSuccessToast('提案求人を追加しました');
        onSuggested?.();
        setSelectedIds(new Set());
        setIsConfirmingSubmission(false);
        onOpenChange(false);
      },
      onError: () => {
        showErrorToast('提案求人の追加に失敗しました');
      },
    });
  }, [selectedResults, suggestMutation, onOpenChange, onSuggested]);

  // 自動で探す（idle から）
  const handleStartDefault = useCallback(() => {
    startPersonaBuild({ candidateProfileId, selectedSources: DEFAULT_SELECTED_SOURCES });
  }, [candidateProfileId, startPersonaBuild]);

  // カスタマイズダイアログからの実行。ペルソナ未構築なら新規構築、構築済みなら再構築。
  const handleCustomizeSubmit = useCallback(
    ({ prompt, selection }: { prompt: string; selection: ReferenceSelection }) => {
      setLastPrompt(prompt);

      const sourceSelection: Partial<SharedAiSourceSelection> = {
        custom_data: selection.customFieldIds,
        candidate_interview_log: selection.interviewLogIds,
      };
      // 個別ID選択があるカテゴリは selectedSources に必ず含める（未指定カテゴリは全件）
      const selectedSources = Array.from(
        new Set([
          ...DEFAULT_SELECTED_SOURCES,
          ...(selection.interviewLogIds.length > 0 ? ['candidate_interview_log'] : []),
        ])
      );
      const freeText = prompt.trim() ? prompt : undefined;

      if (stage === 'idle' || isFailed) {
        startPersonaBuild({ candidateProfileId, selectedSources, sourceSelection, freeText });
      } else {
        rebuildCurrentPersona({ selectedSources, sourceSelection, freeText });
      }
    },
    [candidateProfileId, stage, isFailed, startPersonaBuild, rebuildCurrentPersona]
  );

  return (
    <>
      {/* 確認モーダル・カスタマイズモーダル表示中はメインダイアログを閉じて背後に重ねない */}
      <Dialog open={open && !isConfirmingSubmission && !isCustomizing} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'flex flex-col rounded-sm p-0 shadow-layer3',
            useWideLayout
              ? 'h-[680px] max-h-[90vh] w-[956px] max-w-[95vw]'
              : 'h-auto max-h-[85vh] w-[642px] max-w-[95vw]'
          )}
        >
          {useWideLayout ? (
            <>
              {/* ヘッダー */}
              <div className="flex h-14 shrink-0 items-center justify-between gap-md border-b border-solid border-gray-200 px-lg">
                <DialogTitle className="text-base font-bold">AIおすすめ求人検索結果</DialogTitle>
                <Button
                  color="tertiary"
                  size="md"
                  type="button"
                  onClick={() => setIsCustomizing(true)}
                  disabled={isRerunning}
                >
                  <LuSparkles size={20} aria-hidden="true" />
                  AIをカスタマイズして再検索
                </Button>
              </div>

              {/* ボディ（1カラム） */}
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray',
                  !isRerunning && 'p-lg'
                )}
              >
                {isRerunning ? (
                  <RunStatusPanel
                    isLoading
                    isFailed={false}
                    progressMessage={progressMessage}
                    onRetry={retryPersonaBuild}
                    loadingTitle="AIおすすめ求人を再検索中"
                    fillParent
                  />
                ) : (
                  hasResults &&
                  resultsData && (
                    <RecommendationList
                      results={resultsData.results}
                      selectedIds={selectedIds}
                      onSelectedIdsChange={setSelectedIds}
                    />
                  )
                )}
              </div>

              {/* フッター */}
              <div className="flex h-[74px] shrink-0 items-center justify-between gap-2 border-t border-solid border-gray-200 px-lg">
                {selectedCount > 0 ? (
                  <div className="flex items-center gap-2">
                    <p className="flex items-center gap-[3px] text-main">
                      <span className="text-heading-md">{selectedCount}件</span>
                      <span className="text-body-xs">選択中</span>
                    </p>
                    <Button
                      color="secondary"
                      size="md"
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      disabled={suggestMutation.isPending}
                      aria-label="選択を解除する"
                    >
                      <MdLinkOff size={16} aria-hidden="true" />
                      選択解除
                    </Button>
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <Button
                    color="secondary"
                    size="md"
                    type="button"
                    onClick={() => onOpenChange(false)}
                  >
                    閉じる
                  </Button>
                  {selectedCount > 0 && (
                    <Button
                      color="primary"
                      size="md"
                      type="button"
                      onClick={() => setIsConfirmingSubmission(true)}
                      disabled={!canSubmit}
                    >
                      {suggestMutation.isPending ? '提案中...' : '求人提案する'}
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* コンパクト表示: idle / ローディング / エラー */
            <>
              <DialogTitle className="sr-only">AIでおすすめ求人を探す</DialogTitle>

              {/* 復元中・結果取得中は idle 画面を出さずローディングを表示し、
                  idle→復元→結果 の中間状態がちらつくのを防ぐ */}
              {(isRestoring || (stage === 'search_completed' && isResultsLoading)) && (
                <RunStatusPanel
                  isLoading
                  isFailed={false}
                  progressMessage={null}
                  onRetry={retryPersonaBuild}
                  loadingTitle="AIおすすめ求人を読み込み中"
                />
              )}

              {!isRestoring && stage === 'idle' && (
                <div className="flex flex-col items-center justify-center gap-md p-10">
                  <div className="flex flex-col items-center gap-xs text-center">
                    <div className="flex size-[76px] items-center justify-center rounded-full bg-white shadow-layer1">
                      <LuSparkles size={48} className="text-primary" aria-hidden="true" />
                    </div>
                    <p className="text-heading-xs text-main">
                      AIが候補者に最適な求人をおすすめします
                    </p>
                    <p className="text-body-xs text-placeholder">
                      候補者のプロフィール情報を分析し、マッチ度の高い求人を提案します。
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-xs">
                    <Button
                      color="primary"
                      size="lg"
                      type="button"
                      onClick={handleStartDefault}
                      disabled={isProcessing}
                      aria-label="AI求人推薦を実行"
                    >
                      <LuSparkles size={24} aria-hidden="true" />
                      自動で求人を探す
                    </Button>
                    <Button
                      color="tertiary"
                      size="lg"
                      type="button"
                      onClick={() => setIsCustomizing(true)}
                      disabled={isProcessing}
                    >
                      <LuSlidersHorizontal size={24} aria-hidden="true" />
                      カスタマイズして探す
                    </Button>
                  </div>
                </div>
              )}

              {!isRestoring &&
                stage !== 'idle' &&
                !(stage === 'search_completed' && isResultsLoading) && (
                  <RunStatusPanel
                    isLoading={isProcessing}
                    isFailed={isFailed}
                    progressMessage={progressMessage}
                    errorMessage={errorMessage}
                    onRetry={retryPersonaBuild}
                  />
                )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AIをカスタマイズして検索するモーダル */}
      <AICustomizeDialog
        candidateProfileId={candidateProfileId}
        defaultPrompt={lastPrompt}
        defaultSelection={defaultSelection}
        open={isCustomizing}
        onOpenChange={setIsCustomizing}
        onSubmit={handleCustomizeSubmit}
        isSubmitting={isProcessing}
      />

      {/* 求人提案の最終確認モーダル */}
      <SuggestionConfirmDialog
        open={isConfirmingSubmission}
        onOpenChange={(next) => {
          if (suggestMutation.isPending) return;
          setIsConfirmingSubmission(next);
        }}
        selectedResults={selectedResults}
        isSubmitting={suggestMutation.isPending}
        onConfirm={handleConfirmSuggest}
      />
    </>
  );
}
